import React, { useState } from 'react';
import moment from 'moment';
import { Box, Grid, Stack, Typography } from '@mui/material';
import ChatMessageAction from './ChatMessageAction';
import Reply from './Reply';
import AttachmentFileDownload from './AttachmentFileDownload';
import ImageViewer from './ImageViewer';

const formatChatTime = (date) => {
	if (!date) return '';
	return moment(String(date)).format('LT');
};

const MessageR = ({ message, date, image, file, msgObj }) => {
	const [contextMenuPosition, setContextMenuPosition] = useState(null);

	const handleContextMenu = (event) => {
		event.preventDefault();
		setContextMenuPosition({ top: event.clientY, left: event.clientX });
	};

	const normalizedStatus = msgObj?.seenStatus?.toUpperCase?.() || '';
	const statusLabel =
		normalizedStatus === 'SEEN'
			? 'Seen'
			: normalizedStatus === 'UNSEEN'
				? 'Delivered'
				: normalizedStatus === 'SENDING'
					? 'Sending...'
					: '';

	return (
		<Grid item xs={12}>
			<Stack direction="row" alignItems="flex-start">
				<Grid container justifyContent="flex-end" sx={{ mt: '3px' }}>
					<Grid item xs={2} md={3} xl={4} />

					<Grid item xs={10} md={9} xl={8}>
						<Stack direction="row" justifyContent="flex-end" alignItems="flex-start" spacing={1.25}>
							<ChatMessageAction
								messageObj={msgObj}
								date={date}
								direction="RIGHT"
								contextMenuPosition={contextMenuPosition}
								onContextMenuClose={() => setContextMenuPosition(null)}
							/>
							<Box sx={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-end', maxWidth: '100%' }}>
								<Box
									onContextMenu={handleContextMenu}
									sx={{
										display: 'inline-block',
										wordWrap: 'break-word',
										whiteSpace: 'pre-wrap',
										float: 'right',
										bgcolor: '#4169E1',
										color: 'common.white',
										borderRadius: '6px',
										p: '7px 10px',
										maxWidth: '100%',
									}}
								>
									{image && <ImageViewer file={image} />}
									{file && <AttachmentFileDownload attachment={file} />}

									<Box>
										<Typography variant="chat" sx={{ overflowWrap: 'anywhere', lineHeight: 1.2 }}>
											{message}
										</Typography>
									</Box>
								</Box>
								<Box
									sx={{
										display: 'flex',
										alignItems: 'center',
										justifyContent: 'flex-end',
										gap: 0.75,
										lineHeight: 1,
										mt: 0.25,
										pr: 0.25,
										width: '100%',
									}}
								>
									<Typography variant="date" component="span" sx={{ color: 'text.secondary' }}>
										{formatChatTime(date)}
									</Typography>
									{statusLabel && (
										<Typography variant="date" component="span" sx={{ color: 'text.secondary' }}>
											{statusLabel}
										</Typography>
									)}
								</Box>
							</Box>
						</Stack>
					</Grid>
				</Grid>
			</Stack>
		</Grid>
	);
};

export default MessageR;

export const DeleteMessageR = ({ date }) => {
	return (
		<Grid item xs={12}>
			<Stack direction="row" alignItems="flex-start" spacing={1.25}>
				<Grid container spacing={0.5} justifyContent="flex-end">
					<Grid item xs={2} md={3} xl={4} />

					<Grid item xs={10} md={9} xl={8}>
						<Stack direction="row" justifyContent="flex-end" alignItems="flex-start">
							<Box
								sx={{
									display: 'inline-block',
									wordWrap: 'break-word',
									whiteSpace: 'pre-wrap',
									float: 'right',
									bgcolor: '#9090902b',
									color: '#909090',
									borderRadius: '6px',
									p: '9px 12px 9px 12px',
								}}
							>
								<Box>
									<Typography variant="chat" sx={{ overflowWrap: 'anywhere' }}>
										You deleted this message
									</Typography>
									<Typography variant="date" sx={{ visibility: 'hidden', pl: 1 }}>
										{date && moment(date).format('LT')}
									</Typography>
								</Box>
								<Box
									sx={{
										display: 'inline-block',
										float: 'right',
										lineHeight: '10px',
										marginBottom: '-8px',
										marginRight: '-5px',
										marginTop: '-10px',
									}}
								>
									<Typography variant="date" component="span" align="right">
										{formatChatTime(date)}
									</Typography>
								</Box>
							</Box>
						</Stack>
					</Grid>
				</Grid>
			</Stack>
		</Grid>
	);
};

export const ReplyMessageR = ({ message, date, reply, replyUserName, replyMessage, msgObj }) => {
	const [contextMenuPosition, setContextMenuPosition] = useState(null);

	const handleContextMenu = (event) => {
		event.preventDefault();
		setContextMenuPosition({ top: event.clientY, left: event.clientX });
	};

	const normalizedStatus = msgObj?.seenStatus?.toUpperCase?.() || '';
	const statusLabel =
		normalizedStatus === 'SEEN'
			? 'Seen'
			: normalizedStatus === 'UNSEEN'
				? 'Delivered'
				: normalizedStatus === 'SENDING'
					? 'Sending...'
					: '';

	return (
		<Grid item xs={12}>
			<Stack direction="row" alignItems="flex-start">
				<Grid container justifyContent="flex-end" sx={{ mt: '3px' }}>
					<Grid item xs={2} md={3} xl={4} />

					<Grid item xs={10} md={9} xl={8}>
						<Stack direction="row" justifyContent="flex-end" alignItems="flex-start" spacing={1.25}>
							<ChatMessageAction
								messageObj={msgObj}
								date={date}
								direction="RIGHT"
								contextMenuPosition={contextMenuPosition}
								onContextMenuClose={() => setContextMenuPosition(null)}
							/>
							<Box sx={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-end', maxWidth: '100%' }}>
								<Box
									onContextMenu={handleContextMenu}
									sx={{
										display: 'inline-block',
										wordWrap: 'break-word',
										whiteSpace: 'pre-wrap',
										float: 'right',
										bgcolor: '#4169E1',
										color: 'common.white',
										borderRadius: '6px',
										p: '7px 10px',
										maxWidth: '100%',
									}}
								>
									<Reply replyObj={reply} replyUserName={replyUserName} replyMessage={replyMessage} />
									<Box>
										<Typography variant="chat" sx={{ overflowWrap: 'anywhere', lineHeight: 1.2 }}>
											{message}
										</Typography>
									</Box>
								</Box>
								<Box
									sx={{
										display: 'flex',
										alignItems: 'center',
										justifyContent: 'flex-end',
										gap: 0.75,
										lineHeight: 1,
										mt: 0.25,
										pr: 0.25,
										width: '100%',
									}}
								>
									<Typography variant="date" component="span" sx={{ color: 'text.secondary' }}>
										{formatChatTime(date)}
									</Typography>
									{statusLabel && (
										<Typography variant="date" component="span" sx={{ color: 'text.secondary' }}>
											{statusLabel}
										</Typography>
									)}
								</Box>
							</Box>
						</Stack>
					</Grid>
				</Grid>
			</Stack>
		</Grid>
	);
};
